import tw, { styled } from 'twin.macro';
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import useAdEditorContext from 'hooks/context/ad-editor-context';
import { AD_PLACEMENTS, AdCreativeType } from 'types/adTypes';
import { getVariantIcon } from 'utils/adEditorHelpers';
import ToolTip from 'components/tooltip/ToolTip';
import { Warning, CaretDown, CaretUp, Plus } from '@phosphor-icons/react';
import { AdVariant } from 'types/adEditorTypes';
import { railPreviewUrl } from 'utils/templatePreviewHelpers';
import { cleanInlineSvg } from 'utils/inlineSvgHelpers';
import useCampaignValues from 'hooks/useCampaignValues';
import { useMutation } from 'hooks/sympl-mutation';
import { UPDATE_LIVE_ADS } from 'graphql/campaign-values/mutations';
import { outdatedMarker } from 'utils/liveAdsMessage';
import { OutdatedField } from 'utils/campaignValues';

const MAX_THUMBNAIL_REFETCH_ATTEMPTS = 10;
const THUMBNAIL_REFETCH_INTERVAL_MS = 2500;

interface AdEditorNavBarProps {
  refetchEditorData?: () => void | Promise<unknown>;
}

const AdEditorNavBar: React.FC<AdEditorNavBarProps> = ({
  refetchEditorData,
}) => {
  const {
    setCurrentVariant,
    variants,
    addVariantForPlacement,
    currentVariant,
    markClipSeen,
  } = useAdEditorContext();

  const [showMobileNavBar, toggleMobileNavBar] = useState(false);
  const newVariantRef = useRef<HTMLButtonElement | null>(null);
  const refetchAttemptsRef = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const toggleHandler = () => toggleMobileNavBar(!showMobileNavBar);

  const nonDeletedVariants = useMemo(
    () => variants.filter(({ isDeleted }) => !isDeleted),
    [variants]
  );

  // SYM-6351 (D25, D34). Which ads still show an old job title or location, from the server.
  const { state: campaignValues } = useCampaignValues();
  const outdatedById = useMemo(
    () =>
      new Map(
        (campaignValues?.ads ?? [])
          .filter((ad) => ad.outdated_fields.length)
          .map((ad) => [ad.id, ad])
      ),
    [campaignValues]
  );

  const hasVariantsWithMissingThumbnail = useMemo(
    () => nonDeletedVariants.some((v) => v.path && !v.path.thumbnail_path),
    [nonDeletedVariants]
  );

  useEffect(() => {
    newVariantRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  }, [currentVariant]);

  useEffect(() => {
    if (!hasVariantsWithMissingThumbnail) {
      refetchAttemptsRef.current = 0;
      return;
    }
    if (
      !refetchEditorData ||
      refetchAttemptsRef.current >= MAX_THUMBNAIL_REFETCH_ATTEMPTS
    ) {
      return;
    }

    const runRefetch = () => {
      if (refetchAttemptsRef.current >= MAX_THUMBNAIL_REFETCH_ATTEMPTS) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        return;
      }
      refetchAttemptsRef.current += 1;
      refetchEditorData();
    };

    intervalRef.current = setInterval(
      runRefetch,
      THUMBNAIL_REFETCH_INTERVAL_MS
    );
    runRefetch();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [hasVariantsWithMissingThumbnail, refetchEditorData]);

  return (
    <AdVariantNavbarWrapper>
      <VariantOverview toggleNavbar={showMobileNavBar}>
        {AD_PLACEMENTS.map((placement) => {
          const placementVariants = nonDeletedVariants.filter(
            (variant) => variant.placement === placement
          );

          return (
            <PlacementSection key={placement} id={`ad-placement-${placement}`}>
              <SectionHeader>
                <p>
                  {placement.toUpperCase()}
                  {placementVariants.length > 6 && (
                    <ToolTipWrapper>
                      <ToolTip
                        text="We suggest using max. 6 variants."
                        placement="right"
                        arrow
                      >
                        <Warning weight="bold" size={17} />
                      </ToolTip>
                    </ToolTipWrapper>
                  )}
                </p>
                <AddVariantButton
                  type="button"
                  onClick={() => {
                    addVariantForPlacement(placement);
                    toggleHandler();
                  }}
                  id={`add-variant-button-${placement}`}
                >
                  <Plus weight="bold" size={20} />
                </AddVariantButton>
              </SectionHeader>
              {/* One tablist per placement, so aria-selected below sits on a role that allows it. */}
              <VariantsGrid id={`${placement}-variants`} role="tablist">
                {placementVariants.map((variant) => (
                  <MarkedVariant key={variant.uuid}>
                    <VariantTabButton
                      key={variant.uuid}
                      ref={
                        variant.uuid === currentVariant?.uuid
                          ? newVariantRef
                          : null
                      }
                      isSelected={variant.uuid === currentVariant?.uuid}
                      // Which variant is selected was expressed only as a coloured ring, so the state
                      // existed for a sighted user and for nobody and nothing else. SYM-5921 is a bug
                      // about the selection silently moving, and it could not be observed at all.
                      role="tab"
                      aria-selected={variant.uuid === currentVariant?.uuid}
                      // A stable handle on a rail item. Position is not one: the rail grows as
                      // variants are added, so "the third one" is a different ad after any change.
                      data-variant-id={variant.id ?? ''}
                      onClick={() => {
                        setCurrentVariant(variant);
                        // Looking at it IS having seen it, so the mark clears here rather than on
                        // opening the ad: someone who came in to change the text has been told
                        // nothing and should still find the dot waiting.
                        markClipSeen(variant);
                        toggleHandler();
                      }}
                    >
                      <VariantIcon variant={variant} />
                      {variant.has_unseen_clip && (
                        <NewClipDot
                          aria-label="New video clip you have not looked at"
                          title="New video clip"
                        />
                      )}
                    </VariantTabButton>
                    {variant.id && outdatedById.get(variant.id) && (
                      <OldValueMarker
                        adId={variant.id}
                        fields={outdatedById.get(variant.id)!.outdated_fields}
                        isLive={outdatedById.get(variant.id)!.is_live}
                        liveOutdatedCount={
                          campaignValues?.live_ads.outdated_count ?? 0
                        }
                      />
                    )}
                  </MarkedVariant>
                ))}
              </VariantsGrid>
            </PlacementSection>
          );
        })}
      </VariantOverview>
      <ToggleSelectNavButton onClick={toggleHandler}>
        {!showMobileNavBar ? (
          <CaretDown weight="bold" tw="mt-1" size={36} />
        ) : (
          <CaretUp weight="bold" tw="mt-1" size={36} />
        )}
      </ToggleSelectNavButton>
    </AdVariantNavbarWrapper>
  );
};

/**
 * SYM-6351 (D25, D34). The "old title" / "old location" marker under an ad that ran with a value
 * the campaign has since changed. A live ad can be updated from here, alone or with the others;
 * a stopped one is updated when the campaign restarts (scene 12), so it only says so.
 */
const OldValueMarker: React.FC<{
  adId: number;
  fields: OutdatedField[];
  isLive: boolean;
  liveOutdatedCount: number;
}> = ({ adId, fields, isLive, liveOutdatedCount }) => {
  const { vacancyId, refetch } = useCampaignValues();
  // Where the menu floats: under the marker, on the page itself (test book 4.4 and 7.1, 28 Sep
  // 2026). Inside the ad list it was cut off by the list's own edges -- "date this ad" on a left
  // column, nothing at all where the list scrolls sideways.
  const [at, setAt] = useState<{ left: number; top: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [update, { loading }] = useMutation<
    { updateLiveAds: { updated: number } },
    { vacancyId: number; input: { ad_variant_ids?: number[] } }
  >(UPDATE_LIVE_ADS);

  // Closes on a click elsewhere, or when the list scrolls away from under it.
  useEffect(() => {
    if (!at) return undefined;
    const close = (e: Event) => {
      if (buttonRef.current?.contains(e.target as Node)) return;
      if ((e.target as HTMLElement)?.closest?.('[data-marker-menu]')) return;
      setAt(null);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('scroll', close, true);
    };
  }, [at]);

  const marker = outdatedMarker(fields, liveOutdatedCount);
  if (!marker || !vacancyId) return null;

  const toggle = () => {
    if (at) return setAt(null);
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = 208;
    setAt({
      left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
      top: rect.bottom + 4,
    });
  };

  const send = async (ids?: number[]) => {
    await update({
      variables: { vacancyId, input: ids ? { ad_variant_ids: ids } : {} },
    }).catch(() => undefined);
    setAt(null);
    await refetch();
  };

  return (
    <div tw="mt-1 flex justify-center">
      <button
        ref={buttonRef}
        type="button"
        tw="text-[10px] leading-tight px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300"
        onClick={toggle}
        aria-expanded={!!at}
      >
        {marker.label}
      </button>
      {at &&
        createPortal(
          <div
            data-marker-menu
            style={{
              position: 'fixed',
              left: at.left,
              top: at.top,
              width: 208,
            }}
            tw="z-50 p-2 rounded-md border bg-white shadow-lg flex flex-col gap-1 text-sm"
          >
            {isLive ? (
              <>
                <button
                  type="button"
                  tw="text-left hover:underline"
                  disabled={loading}
                  onClick={() => send([adId])}
                >
                  {marker.updateOne}
                </button>
                {marker.updateAll && (
                  <button
                    type="button"
                    tw="text-left hover:underline"
                    disabled={loading}
                    onClick={() => send()}
                  >
                    {marker.updateAll}
                  </button>
                )}
              </>
            ) : (
              <span tw="text-gray-600">
                This ad is stopped. You can update it when you restart the
                campaign.
              </span>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

const VariantIcon: React.FC<{ variant: AdVariant }> = ({ variant }) => {
  const { currentVariant, templatePreviews, railPreviews } =
    useAdEditorContext();

  // SYM-5880. A v2 template is drawn on the SERVER and laid over the canvas, so none of the three
  // sources below has ever seen it: data_url is a canvas capture, svg is canvas.toSVG() or the
  // last stencil the server generated, and thumbnail_path is the bare photo. With a template
  // selected they all show the PREVIOUS state, so the rail contradicted the canvas next to it.
  //
  // The choice lives in railPreviewUrl() rather than inline: this app has no component tests, so
  // logic kept in here cannot be exercised at all — the same reason previewsAfterValueChange()
  // was extracted.
  // SYM-5975: railPreviews answers for the ads NOT being edited. Before it, they fell through to
  // the three sources above, none of which has ever seen a server-drawn template -- so after a
  // reload the whole rail showed ads without their template until you clicked each one.
  const templateUrl = railPreviewUrl(
    variant,
    currentVariant?.uuid,
    templatePreviews,
    railPreviews
  );

  const isImageVariant =
    variant.path &&
    (variant.creative_type === AdCreativeType.IMAGE || !variant.creative_type);

  // Prefer data_url — it's a pixel-perfect canvas capture with overlays & crop.
  const showDataUrl = isImageVariant && variant.data_url;
  // Fall back to inline SVG (contains text/icons/crop) when data_url isn't
  // available yet.  Skip SVGs with degenerate dimensions (e.g. 1×1) that
  // render as invisible thumbnails — fall through to thumbnail_path instead.
  const hasBrokenSvg =
    variant.svg && /\bviewBox=["']0 0 [01] [01]["']/.test(variant.svg);
  const showSvg =
    variant.svg && !showDataUrl && isImageVariant && !hasBrokenSvg;

  // SYM-6516: variant.svg is stored markup (Fabric 5 export or anything saved through the API),
  // so it is cleaned before it goes into the page. cleanInlineSvg also adds crossorigin="anonymous"
  // on every <image>, matching how fabric.js loads them, so the CORS cache cannot taint the canvas.
  const safeSvg = useMemo(() => {
    if (!showSvg || !variant.svg) return '';
    return cleanInlineSvg(variant.svg);
  }, [showSvg, variant.svg]);

  const sizeCss =
    variant.placement === 'feed'
      ? tw`h-24 w-24 overflow-hidden`
      : tw`h-24 w-14 overflow-hidden`;

  return (
    <picture tw="[svg]:(h-24 w-auto)">
      {templateUrl ? (
        <img src={templateUrl} alt="" css={[sizeCss, tw`object-cover`]} />
      ) : showDataUrl ? (
        <img src={variant.data_url} alt="" css={[sizeCss, tw`object-cover`]} />
      ) : showSvg ? (
        <div
          dangerouslySetInnerHTML={{ __html: safeSvg }}
          css={[sizeCss, { '& svg': { width: '100%', height: '100%' } }]}
        />
      ) : variant.path?.thumbnail_path ? (
        <img
          src={variant.path.thumbnail_path}
          alt=""
          crossOrigin="anonymous"
          css={[sizeCss, tw`object-cover`]}
        />
      ) : (
        getVariantIcon(
          variant.placement,
          variant.creative_type ?? AdCreativeType.IMAGE
        )
      )}
    </picture>
  );
};

const AdVariantNavbarWrapper = styled.aside`
  ${tw`w-full relative left-0 z-20 bg-white h-auto -top-2 lg:(top-0 h-full min-w-[150px] max-w-[250px])`}
  ${tw`grid grid-rows-[auto 1fr] lg:(border-none border-r border-r-gray-200)`}
`;

const VariantOverview = styled.div<{ toggleNavbar?: boolean }>`
  ${tw`grid grid-rows-[repeat(3, 1fr)] pb-0 overflow-y-hidden`}
  ${({ toggleNavbar }) => !toggleNavbar && tw`hidden lg:grid`}
`;

const PlacementSection = styled.div`
  ${tw`flex flex-col overflow-y-auto h-full`}
`;

const SectionHeader = styled.div`
  ${tw`lg:(sticky top-0) flex justify-between text-sm font-medium text-gray-500 items-center border-b bg-gray-50 w-full px-6 py-1`}
`;

const AddVariantButton = styled.button`
  ${tw`p-2 -mr-3 hover:text-black`}
`;

const VariantsGrid = styled.div`
  ${tw`grid grid-cols-1 xl:grid-cols-2 gap-4 overflow-y-auto p-4 place-items-center`}
`;

const VariantTabButton = styled.button<{ isSelected: boolean }>`
  ${tw`relative flex items-center justify-center cursor-pointer`}
  ${({ isSelected }) => isSelected && tw`ring-[3px] ring-indigo-500`}
`;

/**
 * SYM-6076. A clip that arrived while nobody was watching.
 *
 * Bring to Life tells the customer they can close the window and carry on, and the clip really
 * does arrive -- the server attaches it. Until this, the only thing that ever said so was a toast
 * in the tab that started it, so the people most likely to miss their clip were the ones who took
 * our advice. The dot survives reloads and days away, because it is a fact on the server rather
 * than something living in a page.
 */
const MarkedVariant = styled.div`
  ${tw`flex flex-col items-center`}
`;

const NewClipDot = styled.span`
  ${tw`absolute -top-1 -right-1 h-3 w-3 rounded-full bg-indigo-500 ring-2 ring-white`}
`;

const ToolTipWrapper = styled.div`
  ${tw`relative left-1 top-0.5 inline-block`}
`;

const ToggleSelectNavButton = styled.button`
  ${tw`flex justify-center text-gray-500 lg:(hidden flex-none order-none) flex-auto items-center order-1`}
`;

export default AdEditorNavBar;
